import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DataTable } from './common/DataTable';
import { showDeleteConfirmation } from '../lib/sweetAlert';
import { downloadCSV } from '../lib/exportUtils';
import {
  Calendar,
  Clock,
  DollarSign,
  MapPin,
  Search,
  Filter,
  XCircle,
  RefreshCw,
  Download,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';

interface PoojaBooking {
  _id: string;
  id: string;
  bookingId: string;
  user: {
    _id: string;
    name: string;
    email: string;
    phone?: string;
  };
  pooja: {
    _id: string;
    name?: string;
    price?: number;
    currency?: string;
  };
  temple: {
    _id: string;
    name: string;
    address: any;
  };
  bookingDate: string;
  scheduledDate: string;
  bookingTime: string;
  scheduledTime: string;
  amount: number;
  currency: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'rejected';
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded';
  paymentMethod?: 'online' | 'cash';
  contactInfo: {
    name: string;
    email: string;
    phone?: string;
  };
  createdAt: string;
}

interface CompletedBooking {
  id: string;
  _id: string;
  type: 'pooja' | 'event' | 'sponsorship';
  amount: number;
  currency: string;
  contactName?: string;
  contactEmail?: string;
  temple?: { name: string };
  pooja?: { name: string };
  event?: { name: string };
  sponsorship?: { title: string };
  status: string;
  paymentStatus: string;
  createdAt: string;
}

interface BookingFilters {
  status: string;
  paymentStatus: string;
  templeId: string;
  dateRange: string;
  search: string;
}

const PoojaBookingsPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'bookings' | 'completed'>('bookings');
  const [bookings, setBookings] = useState<PoojaBooking[]>([]);
  const [completedBookings, setCompletedBookings] = useState<CompletedBooking[]>([]);
  const [temples, setTemples] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [completedLoading, setCompletedLoading] = useState(true);
  const [filters, setFilters] = useState<BookingFilters>({
    status: 'all',
    paymentStatus: 'all',
    templeId: 'all',
    dateRange: 'all',
    search: ''
  });
  const [completedSearch, setCompletedSearch] = useState('');
  const [completedTypeFilter, setCompletedTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Status Modal State
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<PoojaBooking | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const payload: any = {
        limit: 1000, // Fetch all for clientside filtering/pagination for now to match other pages
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.search && { search: filters.search })
      };

      if (filters.templeId !== 'all') payload.templeId = filters.templeId;

      let bookingsRes;
      if (user?.role === 'temple_admin' && user?.templeId) {
        bookingsRes = await adminApiClient.getBookingsByTemple(user.templeId, payload);
      } else {
        bookingsRes = await adminApiClient.getPoojaBookings(payload);
      }

      if (bookingsRes.success && bookingsRes.data) {
        setBookings(bookingsRes.data.bookings.map((b: any) => ({
          ...b,
          id: b._id || b.id
        })));
      }

      const templesRes = await adminApiClient.getTemples();
      if (templesRes.success && templesRes.data) {
        setTemples(templesRes.data.temples);
      }

    } catch (err: any) {
      toast.error('Error fetching bookings');
    } finally {
      setLoading(false);
    }
  }, [filters, user]);

  const fetchCompletedBookings = useCallback(async () => {
    try {
      setCompletedLoading(true);
      const [poojaRes, eventRes, sponsorRes] = await Promise.all([
        adminApiClient.getPoojaBookings({ limit: 5000 }),
        adminApiClient.getAllEventBookings({ limit: 5000 }),
        adminApiClient.getAllSponsorshipBookings({ limit: 5000 }),
      ]);
      const list: CompletedBooking[] = [];
      (poojaRes.data?.bookings || []).filter((b: any) => b.paymentStatus === 'paid').forEach((b: any) =>
        list.push({ id: b._id, _id: b._id, type: 'pooja', amount: b.amount || 0, currency: b.currency || 'USD', contactName: b.contactInfo?.name || b.user?.name, contactEmail: b.contactInfo?.email || b.user?.email, temple: b.temple, pooja: b.pooja, status: b.status, paymentStatus: b.paymentStatus, createdAt: b.createdAt })
      );
      (eventRes.data?.bookings || []).filter((b: any) => b.paymentStatus === 'paid').forEach((b: any) =>
        list.push({ id: b._id, _id: b._id, type: 'event', amount: b.amount || 0, currency: b.currency || 'USD', contactName: b.user?.name, contactEmail: b.user?.email, temple: b.temple, event: b.event, status: b.status, paymentStatus: b.paymentStatus, createdAt: b.createdAt })
      );
      (sponsorRes.data?.bookings || []).filter((b: any) => b.paymentStatus === 'paid').forEach((b: any) =>
        list.push({ id: b._id, _id: b._id, type: 'sponsorship', amount: b.amount || 0, currency: b.currency || 'USD', contactName: b.user?.name, contactEmail: b.user?.email, temple: b.temple, sponsorship: b.sponsorship, status: b.status, paymentStatus: b.paymentStatus, createdAt: b.createdAt })
      );
      list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setCompletedBookings(list);
    } catch (err) {
      toast.error('Error fetching completed bookings');
    } finally {
      setCompletedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchCompletedBookings();
  }, [fetchData, fetchCompletedBookings]);

  const handleStatusUpdate = async () => {
    if (!selectedBooking) return;
    try {
      const response = await adminApiClient.updateBookingStatus(selectedBooking.id, newStatus, statusNotes);
      if (response.success) {
        toast.success('Status updated successfully');
        setShowStatusModal(false);
        fetchData();
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const handleCancelBooking = async (id: string) => {
    const isConfirmed = await showDeleteConfirmation('Cancel Booking?', 'This usage cannot be undone.');
    if (!isConfirmed) return;

    try {
      const response = await adminApiClient.cancelBooking(id, 'Cancelled by admin');
      if (response.success) {
        toast.success('Booking cancelled');
        fetchData();
      } else {
        toast.error('Failed to cancel booking');
      }
    } catch (err) {
      toast.error('Error cancelling booking');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      rejected: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${styles[status] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  const columns: ColumnDef<PoojaBooking>[] = [
    {
      accessorKey: 'bookingId',
      header: 'Booking Ref',
      cell: (info) => (
        <div>
          <div className="font-medium text-gray-900">#{info.getValue<string>() || info.row.original.id.slice(-6)}</div>
          <div className="text-xs text-gray-500">{new Date(info.row.original.createdAt).toLocaleDateString()}</div>
        </div>
      )
    },
    {
      accessorKey: 'contactInfo',
      header: 'Devotee',
      cell: (info) => (
        <div>
          <div className="font-medium text-gray-900">{info.row.original.contactInfo?.name || 'Unknown'}</div>
          <div className="text-xs text-gray-500">{info.row.original.contactInfo?.email}</div>
        </div>
      )
    },
    {
      accessorKey: 'pooja',
      header: 'Service',
      cell: (info) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{info.row.original.pooja?.name || 'Custom Pooja'}</span>
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <MapPin size={10} /> {info.row.original.temple?.name}
          </span>
        </div>
      )
    },
    {
      accessorKey: 'scheduledDate',
      header: 'Scheduled For',
      cell: (info) => (
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <Calendar size={14} className="text-gray-400" />
          {new Date(info.getValue<string>() || info.row.original.bookingDate).toLocaleDateString()}
          <span className="text-xs text-gray-400">
            {info.row.original.scheduledTime || info.row.original.bookingTime}
          </span>
        </div>
      )
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: (info) => (
        <div>
          <div className="font-medium text-gray-900">${info.getValue<number>().toFixed(2)}</div>
          <span className={`text-[10px] px-1.5 rounded uppercase ${info.row.original.paymentStatus === 'paid' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
            {info.row.original.paymentStatus}
          </span>
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => getStatusBadge(info.getValue<string>())
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex gap-2">
          <button title="Update Status" onClick={() => { setSelectedBooking(info.row.original); setNewStatus(info.row.original.status); setShowStatusModal(true); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
            <RefreshCw size={16} />
          </button>
          {info.row.original.status !== 'cancelled' && (
            <button title="Cancel Booking" onClick={() => handleCancelBooking(info.row.original.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
              <XCircle size={16} />
            </button>
          )}
        </div>
      )
    }
  ];

  const filteredBookings = bookings.filter(b => {
    if (filters.paymentStatus !== 'all' && b.paymentStatus !== filters.paymentStatus) return false;

    if (filters.dateRange !== 'all') {
      const d = new Date(b.bookingDate);
      const now = new Date();
      if (filters.dateRange === 'today') return d.toDateString() === now.toDateString();
      // Add other date logic if needed
    }
    return true;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Bookings</h1>
          <p className="text-sm text-gray-500">Manage all pooja, event, and sponsorship reservations</p>
        </div>
        <button onClick={() => { fetchData(); fetchCompletedBookings(); }} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
          <RefreshCw size={20} className={loading || completedLoading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('bookings')}
          className={`px-6 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'bookings' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          All Bookings
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`px-6 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'completed' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Completed Bookings
        </button>
      </div>

      {activeTab === 'bookings' && (
        <>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Bookings', val: bookings.length, icon: Calendar, color: 'blue' },
          { label: 'Pending', val: bookings.filter(b => b.status === 'pending').length, icon: Clock, color: 'yellow' },
          { label: 'Revenue', val: `$${bookings.reduce((acc, curr) => acc + curr.amount, 0).toLocaleString()}`, icon: DollarSign, color: 'green' },
          { label: 'Cancelled', val: bookings.filter(b => b.status === 'cancelled').length, icon: XCircle, color: 'red' },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900">{stat.val}</p>
            </div>
            <div className={`p-3 bg-${stat.color}-50 text-${stat.color}-600 rounded-lg`}>
              <stat.icon size={24} />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 space-y-4">
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search bookings..."
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none"
            />
          </div>
          <select
            value={filters.status}
            onChange={e => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-orange-500/20"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${showFilters ? 'bg-orange-50 border-orange-200 text-orange-700' : ''}`}>
            <Filter size={18} /> More Filters
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t">
            <select value={filters.paymentStatus} onChange={e => setFilters({ ...filters, paymentStatus: e.target.value })} className="px-3 py-2 border rounded-lg outline-none bg-white">
              <option value="all">All Payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Unpaid</option>
            </select>
            {user?.role !== 'temple_admin' && (
              <select value={filters.templeId} onChange={e => setFilters({ ...filters, templeId: e.target.value })} className="px-3 py-2 border rounded-lg outline-none">
                <option value="all">All Temples</option>
                {temples.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          data={filteredBookings}
          columns={columns}
          loading={loading}
          enableClientSidePagination={true}
        />
      </div>
      </>
      )}

      {activeTab === 'completed' && (
        <>
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 mb-4">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search customer, item, temple..."
                  value={completedSearch}
                  onChange={e => setCompletedSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-lg outline-none"
                />
              </div>
              <select value={completedTypeFilter} onChange={e => setCompletedTypeFilter(e.target.value)} className="px-2 py-1.5 text-sm border rounded-lg outline-none">
                <option value="all">All Types</option>
                <option value="pooja">Pooja</option>
                <option value="event">Event</option>
                <option value="sponsorship">Sponsorship</option>
              </select>
              <button
                onClick={() => {
                  const rows = completedBookings
                    .filter(b => completedTypeFilter === 'all' || b.type === completedTypeFilter)
                    .map(b => ({ Type: b.type, Customer: b.contactName || '', Email: b.contactEmail || '', Item: b.pooja?.name || b.event?.name || b.sponsorship?.title || '', Temple: b.temple?.name || '', Amount: b.amount, Currency: b.currency, Status: b.status, Payment: b.paymentStatus, Date: new Date(b.createdAt).toLocaleString() }));
                  downloadCSV(rows, `completed_bookings_${new Date().toISOString().split('T')[0]}.csv`);
                  toast.success('Exported');
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50"
              >
                <Download size={16} /> Export CSV
              </button>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <DataTable
              data={completedBookings.filter(b => {
                const s = completedSearch.toLowerCase();
                const matchSearch = !s || (b.contactName || '').toLowerCase().includes(s) || (b.contactEmail || '').toLowerCase().includes(s) || (b.pooja?.name || b.event?.name || b.sponsorship?.title || '').toLowerCase().includes(s);
                const matchType = completedTypeFilter === 'all' || b.type === completedTypeFilter;
                return matchSearch && matchType;
              })}
              columns={[
                { accessorKey: 'type', header: 'Type', cell: (info) => { const t = info.getValue<string>() as any; const colors: any = { pooja: 'bg-orange-100 text-orange-800', event: 'bg-blue-100 text-blue-800', sponsorship: 'bg-green-100 text-green-800' }; return <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${colors[t] || 'bg-gray-100'}`}>{t}</span>; }},
                { accessorKey: 'contactName', header: 'Customer', cell: (info) => <div><div className="font-medium text-gray-900">{info.getValue<string>() || 'N/A'}</div><div className="text-xs text-gray-500">{info.row.original.contactEmail}</div></div> },
                { id: 'item', header: 'Item', cell: ({ row }) => { const b = row.original; return <span className="text-sm text-gray-700">{b.pooja?.name || b.event?.name || b.sponsorship?.title || '—'}</span>; }},
                { accessorKey: 'temple', header: 'Temple', cell: (info) => <span className="text-sm text-gray-700">{info.getValue<any>()?.name || 'N/A'}</span> },
                { accessorKey: 'amount', header: 'Amount', cell: (info) => <span className="font-bold text-gray-900">${(info.getValue<number>() || 0).toLocaleString()}</span> },
                { accessorKey: 'createdAt', header: 'Date', cell: (info) => <span className="text-sm text-gray-500">{new Date(info.getValue<string>()).toLocaleDateString()}</span> },
              ]}
              loading={completedLoading}
              enableClientSidePagination={true}
            />
          </div>
        </>
      )}

      {showStatusModal && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Update Status</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Status</label>
                <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none">
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={3} value={statusNotes} onChange={e => setStatusNotes(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" placeholder="Optional notes..."></textarea>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setShowStatusModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button onClick={handleStatusUpdate} className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700">Update</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoojaBookingsPage;
